import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, X, Users, AtSign, Smile, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '@/config/api';

const EMOJI_QUICK_PICKS = ['👍', '👎', '❤️', '😊', '🎉', '🔥', '👀', '✅'];

const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  
  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const MessageBubble = ({ message, isOwn, showSender }) => {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}>
      <div className={`max-w-[80%] ${isOwn ? 'order-2' : 'order-1'}`}>
        {showSender && !isOwn && (
          <div className="flex items-center gap-1 mb-1 ml-1">
            <div 
              className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-medium"
              style={{ backgroundColor: message.sender_color || '#6366F1' }}
            >
              {message.sender_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <span className="text-xs text-gray-500 font-medium">{message.sender_name}</span>
          </div>
        )}
        <div
          className={`px-3 py-2 rounded-2xl ${
            isOwn 
              ? 'bg-blue-600 text-white rounded-br-md' 
              : 'bg-gray-100 text-gray-900 rounded-bl-md'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        </div>
        <span className={`text-xs text-gray-400 mt-0.5 block ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>
          {formatTime(message.created_at)}
        </span>
      </div>
    </div>
  );
};

export const ChatPanel = ({ 
  isOpen, 
  onClose, 
  whiteboardId, 
  token, 
  currentUserId,
  collaborators = [],
  onNewMessage // Called when new message arrives via WebSocket
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!whiteboardId || whiteboardId === 'new' || !token) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/messages?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoading(false);
    }
  }, [whiteboardId, token]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!whiteboardId || whiteboardId === 'new' || !token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/messages/unread`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [whiteboardId, token]);

  // Mark messages as read
  const markAsRead = useCallback(async () => {
    if (!whiteboardId || whiteboardId === 'new' || !token) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/messages/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [whiteboardId, token]);

  // Load messages when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchMessages();
      markAsRead();
    }
  }, [isOpen, fetchMessages, markAsRead]);

  // Fetch unread count periodically when panel is closed
  useEffect(() => {
    if (!isOpen && whiteboardId && whiteboardId !== 'new') {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Check every 30s
      return () => clearInterval(interval);
    }
  }, [isOpen, whiteboardId, fetchUnreadCount]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (onNewMessage) {
      const handleNewMessage = (message) => {
        setMessages(prev => {
          if (prev.find(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
        
        if (!isOpen) {
          setUnreadCount(prev => prev + 1);
        }
      };
      
      // This would be called from parent component
      return () => {};
    }
  }, [onNewMessage, isOpen]);

  // Send message
  const handleSend = async () => {
    if (!newMessage.trim() || !whiteboardId || whiteboardId === 'new' || !token) return;
    
    setIsSending(true);
    try {
      // Extract mentions from message
      const mentionMatches = newMessage.match(/@(\w+)/g) || [];
      const mentionedUserIds = mentionMatches
        .map(m => {
          const name = m.slice(1).toLowerCase();
          const user = collaborators.find(c => 
            c.name?.toLowerCase().includes(name) || c.email?.toLowerCase().includes(name)
          );
          return user?.id;
        })
        .filter(Boolean);

      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newMessage.trim(),
          mentions: mentionedUserIds
        })
      });

      if (response.ok) {
        const sentMessage = await response.json();
        setMessages(prev => [...prev, sentMessage]);
        setNewMessage('');
        setShowMentions(false);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle @ mentions
  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Check for @ trigger
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.slice(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt.toLowerCase());
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  // Insert mention
  const insertMention = (user) => {
    const lastAtIndex = newMessage.lastIndexOf('@');
    const newValue = newMessage.slice(0, lastAtIndex) + `@${user.name} `;
    setNewMessage(newValue);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  // Insert emoji
  const insertEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  // Filter collaborators for mentions
  const filteredCollaborators = collaborators.filter(c => 
    c.name?.toLowerCase().includes(mentionSearch) || 
    c.email?.toLowerCase().includes(mentionSearch)
  );

  // Group messages by sender for visual grouping
  const shouldShowSender = (message, index) => {
    if (index === 0) return true;
    const prevMessage = messages[index - 1];
    return prevMessage.sender_id !== message.sender_id;
  };

  return (
    <>
      {/* Chat Toggle Button (shown when panel is closed) */}
      {!isOpen && (
        <button
          onClick={() => onClose()} // This will toggle open
          className="fixed bottom-24 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all z-50"
          data-testid="chat-toggle-btn"
        >
          <MessageCircle size={24} />
          {unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
        </button>
      )}

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-14 bottom-0 w-80 bg-white border-l border-gray-200 shadow-xl flex flex-col z-40"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <MessageCircle size={18} className="text-blue-600" />
                <h3 className="font-semibold text-gray-900">Chat</h3>
                {collaborators.length > 0 && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Users size={12} />
                    {collaborators.length + 1}
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="w-8 h-8 p-0"
                onClick={onClose}
                data-testid="close-chat-btn"
              >
                <X size={18} />
              </Button>
            </div>

            {/* Messages */}
            <div 
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-1"
              data-testid="chat-messages"
            >
              {isLoading ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <MessageCircle size={40} className="mb-2 opacity-50" />
                  <p className="text-sm">No messages yet</p>
                  <p className="text-xs mt-1">Start a conversation!</p>
                </div>
              ) : (
                messages.map((message, index) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isOwn={message.sender_id === currentUserId}
                    showSender={shouldShowSender(message, index)}
                  />
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Mention suggestions */}
            <AnimatePresence>
              {showMentions && filteredCollaborators.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute bottom-20 left-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 max-h-40 overflow-y-auto"
                >
                  {filteredCollaborators.map(user => (
                    <button
                      key={user.id}
                      onClick={() => insertMention(user)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                    >
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
                        style={{ backgroundColor: user.color || '#6366F1' }}
                      >
                        {user.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="text-sm font-medium">{user.name}</div>
                        <div className="text-xs text-gray-400">{user.email}</div>
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Emoji picker */}
            <AnimatePresence>
              {showEmojiPicker && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute bottom-20 left-4 bg-white rounded-lg shadow-lg border border-gray-200 p-2"
                >
                  <div className="flex gap-1">
                    {EMOJI_QUICK_PICKS.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => insertEmoji(emoji)}
                        className="w-8 h-8 hover:bg-gray-100 rounded flex items-center justify-center text-lg"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={newMessage}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="w-full px-3 py-2 pr-16 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    rows={1}
                    style={{ minHeight: '40px', maxHeight: '100px' }}
                    data-testid="chat-input"
                  />
                  <div className="absolute right-2 bottom-2 flex items-center gap-1">
                    <button
                      onClick={() => setShowMentions(!showMentions)}
                      className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
                      title="Mention someone"
                    >
                      <AtSign size={16} />
                    </button>
                    <button
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
                      title="Add emoji"
                    >
                      <Smile size={16} />
                    </button>
                  </div>
                </div>
                <Button
                  onClick={handleSend}
                  disabled={!newMessage.trim() || isSending}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-3 h-10"
                  data-testid="send-message-btn"
                >
                  <Send size={18} />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Export a hook to receive WebSocket messages
export const useChatMessages = (setMessages, isOpen, setUnreadCount) => {
  const handleNewMessage = useCallback((message) => {
    setMessages(prev => {
      if (prev.find(m => m.id === message.id)) return prev;
      return [...prev, message];
    });
    
    if (!isOpen) {
      setUnreadCount(prev => prev + 1);
    }
  }, [setMessages, isOpen, setUnreadCount]);

  return handleNewMessage;
};
